<script setup>
import { useMiscStore } from '@/stores/misc';
import LikeButtonComponent from './LikeButtonComponent.vue';
import TagComponent from "@/components/TagComponent.vue";
import {BuildingOfficeIcon, MapPinIcon} from "@heroicons/vue/16/solid";
import CommentCountComponent from "@/components/shared/CommentCountComponent.vue";
import {computed} from "vue";

const misc = useMiscStore();

const props = defineProps({
  layout: {
    default: false,
    type: Boolean,
  },
  showImage: {
    default: false,
    type: Boolean,
  },
  businesses: {
    default: () => [],
    type: [Array, Object],
    validator: (value) => {
      // Allow both arrays and reactive objects with .value
      return Array.isArray(value) || (value && typeof value === 'object' && 'value' in value);
    }
  },
});

// Normalize businesses data to always be an array
const normalizedBusinesses = computed(() => {
  const businesses = props.businesses?.value || props.businesses || [];
  console.log('[PostList] Normalized businesses:', businesses);
  return Array.isArray(businesses) ? businesses : [];
});

const setCurrentSectorAndCategory = (sector, category) => {
  if (sector && category) {
    misc.setCurrentSector(sector);
    misc.setCurrentCategory(category);
  }
};
</script>

<template>
  <div class="w-full">
    <div
      v-if="normalizedBusinesses.length === 0"
      class="text-center py-8"
    >
      <p class="text-gray-500">
        No businesses to display
      </p>
      <p class="text-sm text-gray-400">
        Received data: {{ JSON.stringify(props.businesses) }}
      </p>
    </div>
    
    <ul
      v-else
      :class="[
        'list-none mt-4 mb-3',
        props.layout ? 'grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4' : 'grid-cols-1',
      ]"
    >
      <li
        v-for="(business, index) in normalizedBusinesses"
        :key="business?.id || index"
        v-bind="business?.id ? { id: `business-${business.id}` } : {}"
        :class="[props.layout ? 'mb-0' : 'mb-3']"
      >
        <div
          id="card"
        >
          <div
            style="min-height: 230px; max-height: 230px; object-fit: cover;"
            :class="[
              'card-image-top link-image-preview rounded-t-xl relative overflow-hidden',
              props.showImage ? 'block' : 'hidden',
            ]"
          >
            <router-link
              :to="business?.sector?.slug && business?.category?.slug ?
                `/${business.sector.slug.toLowerCase()}/${business.category.slug.toLowerCase()}/${business.id}` : '#'"
              target="_self"
              @click="
                setCurrentSectorAndCategory(business.sector, business.category)
              "
            >
              <span
                class="ay-overlay overlay-success absolute flex justify-center items-center top-0 bg-teal-950/50 w-full h-full"
                style="z-index: 1"
              />
              <img
                :src="business.baseImageUrl"
                :alt="business.name"
                class="rounded-t-xl w-full ay-img-scaled object-cover"
                style="height: 230px;"
                decoding="async"
                importance="low"
                :title="business.name"
                loading="lazy"
              >
            </router-link>
          </div>

          <div
            id="content-title"
            :class="[
              'w-full mb-2 border-b p-4 border-red-100 bg-white',
              props.showImage ? 'rounded-b-xl' : 'rounded-xl'
            ]"
          >
            <h3
              class="h-6 text-lg font-bold overflow-hidden whitespace-nowrap overflow-ellipsis"
            >
              <router-link
                :to="`${business.sector.slug.toLowerCase()}/${business.category.slug.toLowerCase()}/${
                  business.id
                }`"
                target="_self"
                class="text-gray-600 font-semibold hover:text-red-600 transition-all duration-500"
                @click="
                  setCurrentSectorAndCategory(business.sector, business.category)
                "
              >
                {{ business.name }}
              </router-link>
            </h3>
          </div>

          <div
            id="content-metadata"
            class="text-xs mb-1 p-4 bg-white/80 rounded-xl"
          >
            <div class="flex items-center justify-start">
              <BuildingOfficeIcon
                id="bookmark"
                data-toggle="tooltip"
                class="h-4 w-4 text-left text-yellow-300 cursor-pointer -ml-1 mr-1 inline"
                aria-hidden="true"
                style="cursor:pointer;"
              />
              <span class="text-gray-400">
                {{ business?.region?.name || 'N/A' }}/{{ business?.city?.name || 'N/A' }}
              </span>
            </div>
            <div class="flex items-center justify-start mt-1 mb-3">
              <MapPinIcon
                id="bookmark"
                data-toggle="tooltip"
                class="h-4 w-4 text-left text-green-300 cursor-pointer -ml-1 mr-1 inline"
                aria-hidden="true"
                style="cursor:pointer;"
              />
              <span class="text-gray-400 overflow-hidden whitespace-nowrap overflow-ellipsis ">
                {{ business?.address }}
              </span>
            </div>

            <div class="text-right">
              <router-link
                :to="{
                  name: 'Category',
                  params: {
                    sector: business.sector.slug,
                    category: business.category.slug
                  }
                }"
                class="tag self-center align-baseline"
                @click="setCurrentSectorAndCategory(business.sector, business.category)"
              >
                <TagComponent
                  :title="business.category?.name"
                  :class="`text-[.67rem] md:mt-2 rounded-md px-1.5 py-[2px]`"
                  :bg-color="business.category?.background"
                  :color="business.category?.color"
                />
              </router-link>
            </div>
          </div>

          <div
            id="card-footer"
            class="w-full p-4 border-t border-green-50 bg-green-100/20 rounded-xl shadow-sm"
          >
            <div class="flex flex-col text-xs">
              <div class="flex flex-wrap justify-between items-center">
                <div class="items-center flex flex-grow no-more">
                  <LikeButtonComponent
                    :id="business.id"
                    :count="business.likeCount"
                    :user-has-liked="business.liked"
                    from="all"
                  />
                  <CommentCountComponent
                    :id="business.id"
                    :count="business.reviewCount"
                    :user-has-reviewed="business.reviewed"
                    from="all"
                  />
                </div>

                <div class="flex items-center justify-end flex-grow">
                  <span class="text-gray-400/80 font-semibold text-xs mr-md-2">
                    {{ business.updatedAt }}
                  </span>

                  <router-link
                    :to="`/users/${business.creator.id}/businesses`"
                    :title="business.creator.name"
                  >
                    <img
                      :src="business.creator.profile.avatar"
                      class="w-7 h-7 rounded-full mr-0 ml-2 text-success"
                      alt=""
                    >
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
      <!--      <li class="rounded border mb-3 p-1">-->
      <!--        <div class="media-body">-->
      <!--          No Contribution yet.-->
      <!--        </div>-->
      <!--      </li>-->
    </ul>
  </div>
</template>

<style scoped></style>
